import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell from './AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { fetchMe, login } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

export default function SignIn() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { hydrate } = useAuth();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const fd = new FormData(event.currentTarget);
      const email = String(fd.get('email') ?? '').trim();
      const password = String(fd.get('password') ?? '');
      await login(email, password);
      const me = await fetchMe();
      hydrate(me);
      const redirectTo = params.get('redirect') ?? '/';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      stamp="Private · sign in"
      title="HouseOps"
      subtitle="The operational system of record for one home and its grounds."
      footer={
        <span>
          Need an account?{' '}
          <Link to="/auth/register" className="text-primary hover:underline">
            Create one
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="sign-in-form">
        <div className="space-y-2">
          <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-wider">
            Email
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            className="h-11 text-base"
            data-testid="email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-wider">
            Password
          </Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            className="h-11 text-base"
            data-testid="password"
          />
        </div>
        {error ? (
          <p className="text-sm text-warning font-medium" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" className="w-full h-11 text-base" disabled={pending} data-testid="submit">
          {pending ? 'Signing in…' : 'Sign in'}
        </Button>
        <p className="text-xs text-center sm:text-left">
          <Link to="/auth/forgot" className="text-muted-foreground hover:text-foreground">
            Forgot your password?
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
